import {
  AdvertisementIntegrationStatus,
  AdvertisementListItem,
  AdvertisementStatus,
} from '../../features/advertisements/models/advertisement.model';
import { InvoiceListItem, InvoiceStatus } from '../../features/invoices/models/invoice.model';
import { OrderListItem, OrderProgressCode } from '../../features/orders/models/order.model';
import { Product } from '../../features/products/models/product.model';
import { providerLabel } from '../../shared/utils/import-per-channel';
import { shownInvoiceStatus } from '../../shared/utils/invoice-status';
import { ORDER_PROGRESS_LABELS } from '../../shared/utils/order-progress';
import { SearchKind, SearchPreview, SearchTone } from './command-palette.model';

// Selo + prévia de cada entidade da busca, montados SÓ com a linha que a lista paginada já devolve
// (a busca não faz chamada nova por item realçado). Funções puras: o serviço chama, o spec testa.

export interface EntityDetail {
  meta?: { text: string; tone: SearchTone };
  preview: SearchPreview;
}

/** Filtros em pílula do modal, na ordem da tela. `kinds` vazio = Tudo. */
export const SEARCH_SCOPES: { id: string; label: string; kinds: SearchKind[] }[] = [
  { id: 'all', label: 'Tudo', kinds: [] },
  { id: 'order', label: 'Pedidos', kinds: ['order'] },
  { id: 'product', label: 'Produtos', kinds: ['product'] },
  { id: 'ad', label: 'Anúncios', kinds: ['ad'] },
  { id: 'invoice', label: 'Notas', kinds: ['invoice'] },
  { id: 'page', label: 'Páginas', kinds: ['page'] },
];

const PROGRESS_TONE: Partial<Record<OrderProgressCode, SearchTone>> = {
  [OrderProgressCode.ErpRefused]: 'bad',
  [OrderProgressCode.InvoiceChannelFailed]: 'bad',
  [OrderProgressCode.InvoiceDenied]: 'bad',
  [OrderProgressCode.InvoiceNotIssued]: 'bad',
  [OrderProgressCode.StockShortage]: 'bad',
  [OrderProgressCode.StockCheck]: 'warn',
  [OrderProgressCode.AwaitingDispatch]: 'info',
  [OrderProgressCode.Dispatched]: 'ok',
};

export function orderDetail(o: OrderListItem): EntityDetail {
  const meta = o.progress != null
    ? { text: ORDER_PROGRESS_LABELS[o.progress], tone: PROGRESS_TONE[o.progress] ?? ('neutral' as SearchTone) }
    : undefined;
  const invoice = o.invoice?.number ? `${o.invoice.number}${o.invoice.series ? '-' + o.invoice.series : ''}` : 'Sem nota';
  return {
    meta,
    preview: {
      facts: [
        { label: 'Valor', value: money(o.totalAmount) },
        { label: 'Data', value: dateTime(o.orderDate) },
        { label: 'Canal', value: providerLabel(o.provider) },
        { label: 'Nota fiscal', value: invoice },
      ],
      primary: 'Abrir pedido',
      actions: [{ label: 'Copiar número do pedido', copy: o.externalOrderId }],
    },
  };
}

export function productDetail(p: Product): EntityDetail {
  const companies = p.productCompanies ?? [];
  const stock = companies.reduce((s, pc) => s + (pc.quantity ?? 0), 0);
  const ads = p.adCount ?? 0;
  return {
    meta: stock > 0 ? { text: `Estoque ${stock}`, tone: 'ok' } : { text: 'Sem estoque', tone: 'bad' },
    preview: {
      facts: [
        { label: 'Estoque total', value: String(stock) },
        { label: 'Preço', value: money(p.price) },
        { label: 'Anúncios', value: String(ads) },
        { label: 'Marca', value: p.brand || 'Não informada' },
      ],
      list: companies.length > 1
        ? {
            title: 'Estoque por loja',
            rows: companies.map((pc) => ({
              label: pc.companyName || `Empresa ${pc.companyId}`,
              value: String(pc.quantity ?? 0),
              tone: (pc.quantity ?? 0) > 0 ? undefined : 'bad',
            })),
          }
        : undefined,
      primary: 'Abrir produto',
      actions: [{ label: 'Copiar SKU', copy: p.sku }],
    },
  };
}

export function adDetail(a: AdvertisementListItem): EntityDetail {
  const status = adStatusLabel(a.status);
  const price = a.priceMax && a.priceMax > a.price ? `${money(a.price)} a ${money(a.priceMax)}` : money(a.price);
  const channels = a.channels ?? [];
  return {
    meta: { text: status.text, tone: status.tone },
    preview: {
      facts: [
        { label: 'Preço', value: price },
        { label: 'Estoque', value: String(a.availableStock ?? 0) },
        { label: 'Vendas em 30 dias', value: String(a.sales30d ?? 0) },
        { label: 'Qualidade', value: a.qualityScore != null ? String(a.qualityScore) : 'Sem nota' },
      ],
      list: channels.length
        ? {
            title: channels.length === 1 ? 'Canal' : 'Canais',
            rows: channels.map((c) => {
              const s = channelStatusLabel(c.status);
              return { label: providerLabel(c.provider), value: s.text, tone: s.tone };
            }),
          }
        : undefined,
      primary: 'Abrir anúncio',
      actions: [{ label: 'Copiar SKU', copy: a.sku }],
    },
  };
}

export function invoiceDetail(i: InvoiceListItem): EntityDetail {
  const status = invoiceStatusLabel(shownInvoiceStatus(i));
  const actions: SearchPreview['actions'] = [];
  if (i.orderId) actions.push({ label: 'Ver pedido', route: `/pedidos/${i.orderId}` });
  if (i.accessKey) actions.push({ label: 'Copiar chave de acesso', copy: i.accessKey });
  return {
    meta: { text: status.text, tone: status.tone },
    preview: {
      facts: [
        { label: 'Valor', value: money(i.totalAmount) },
        { label: 'Emissão', value: i.issuedAt ? dateTime(i.issuedAt) : 'Não emitida' },
        { label: 'Pedido', value: i.orderExternalId || 'Sem pedido' },
        { label: 'Canal', value: providerLabel(i.provider) },
      ],
      primary: 'Abrir nota fiscal',
      actions,
    },
  };
}

function adStatusLabel(status: AdvertisementStatus | string): { text: string; tone: SearchTone } {
  const value = typeof status === 'string' ? status : AdvertisementStatus[status];
  switch (value) {
    case 'Published': return { text: 'Publicado', tone: 'ok' };
    case 'Paused': return { text: 'Pausado', tone: 'warn' };
    case 'Closed': return { text: 'Encerrado', tone: 'neutral' };
    case 'Ready': return { text: 'Pronto', tone: 'info' };
    case 'Draft': return { text: 'Rascunho', tone: 'neutral' };
    default: return { text: value || 'Sem status', tone: 'neutral' };
  }
}

function channelStatusLabel(status: AdvertisementIntegrationStatus | string): { text: string; tone: SearchTone } {
  const value = typeof status === 'string' ? status : AdvertisementIntegrationStatus[status];
  switch (value) {
    case 'Published': return { text: 'Publicado', tone: 'ok' };
    case 'Paused': return { text: 'Pausado', tone: 'warn' };
    case 'Failed': return { text: 'Com falha', tone: 'bad' };
    default: return { text: 'Pendente', tone: 'neutral' };
  }
}

function invoiceStatusLabel(status: InvoiceStatus): { text: string; tone: SearchTone } {
  switch (status) {
    case InvoiceStatus.Authorized: return { text: 'Autorizada', tone: 'ok' };
    case InvoiceStatus.Cancelled: return { text: 'Cancelada', tone: 'neutral' };
    case InvoiceStatus.Denied: return { text: 'Denegada', tone: 'bad' };
    default: return { text: 'Pendente', tone: 'warn' };
  }
}

function money(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value ?? 0);
}

function dateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '-';
  // O Intl põe vírgula entre data e hora ("29/10, 22:10"); a tela usa "29/10 22:10".
  return new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })
    .format(d)
    .replace(',', '');
}

/** Tipo do item; o que foi gravado antes do `kind` (favoritos, mais usados) sai do prefixo do id. */
export function searchKind(item: { id: string; kind?: SearchKind }): SearchKind {
  if (item.kind) return item.kind;
  const prefix = item.id.split(':')[0];
  switch (prefix) {
    case 'order': return 'order';
    case 'product': return 'product';
    case 'ad': return 'ad';
    case 'invoice': return 'invoice';
    case 'page':
    case 'action':
    case 'view': return 'page';
    default: return 'config';
  }
}

/** "agora", "há 5 min", "há 2 h", "ontem", "há 3 dias" para os abertos recentemente. */
export function relativeTime(then: number, now: number): string {
  const minutes = Math.floor((now - then) / 60_000);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `há ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `há ${hours} h`;
  const days = Math.floor(hours / 24);
  return days === 1 ? 'ontem' : `há ${days} dias`;
}
